import { getDetachedDay, getAttachedDate } from "@laborchart-modules/common/dist/datetime";
import { PageTitle, type CustomField } from "@/react/prop-types";
import { processCustomFields } from "@/react/shared/custom-field-utils";
import type { AssignmentFormValues, AssignmentListData, OverTimeRates } from "./assignment/types";
import type { RequestFormValues, RequestListData } from "./request/types";
import type {
   ActivitiesResponseProps,
   BaggageTimeKey,
   CommonPayloadForRequestAndAssignment,
   ProjectOptions,
} from "./types";
import { timeOptions } from "@/lib/utils/timezones";
import { dayNameToNumberMap, numberToDayNameMap } from "@/react/shared/constants";
import Resizer from "react-image-file-resizer";
import type { SerializedAttachment } from "@laborchart-modules/common/dist/rethink/serializers/attachment-serializer";
import type { AttachmentsData } from "./attachments/types";
import type { TagData } from "./tags/types";
import type { ActivityCategory } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import type { ActivityEntityType } from "@laborchart-modules/common/dist/reql-builder/procedures/find-activity";
import { CurrencyFormatter } from "@procore/globalization-toolkit";
import { translationsLabelsActivityCard } from "./constants";
import { CustomFieldType } from "@laborchart-modules/common/dist/rethink/schemas/common/custom-field-type";
import {
   renderAdd,
   renderChange,
   renderColorAdd,
   renderColorChange,
   renderColorRemove,
   renderRemove,
} from "./activities/activity-helper";

import { addWeeks, isAfter } from "date-fns";
import type { Dispatch, SetStateAction } from "react";
import type { useI18nContext } from "@procore/core-react";
import type { AssignmentWorkDays } from "@/models/assignment";

export const NEXT_GROUP_ID_PREFIX = "children-of-";

export type CategoryTieredSelectOption = {
   id: string;
   label: string;
   groupId: string | null;
   nextGroupId?: string;
};

/**
 * Builds the full path from root to the selected item by walking up the ancestor chain.
 * Supports arbitrary nesting depth (child -> parent -> grandparent -> ... -> root).
 */
export const buildTieredSelectPath = (
   options: CategoryTieredSelectOption[],
   selectedId: string,
): CategoryTieredSelectOption[] => {
   const optionsById = new Map(options.map((o) => [o.id, o]));
   const path: CategoryTieredSelectOption[] = [];
   let current = optionsById.get(selectedId);

   while (current) {
      path.unshift(current);
      if (current.groupId) {
         const parentId = current.groupId.replace(NEXT_GROUP_ID_PREFIX, "");
         current = optionsById.get(parentId);
      } else {
         break;
      }
   }

   return path;
};

/**
 * Maps a TieredSelect selection back to the current 2-level category/subcategory form fields.
 * This is the ONLY function that assumes a max of 2 levels.
 * When migrating to leaf_category_id, remove this function.
 */
export const syncFormCategoryFields = (
   options: CategoryTieredSelectOption[],
   selectedValue: CategoryTieredSelectOption[],
   setFieldValue: (field: string, value: any) => void,
) => {
   if (selectedValue.length === 0) {
      setFieldValue("category", null);
      setFieldValue("subcategory", null);
      return;
   }

   const selected = selectedValue[selectedValue.length - 1];

   if (selected.groupId) {
      // Selected item is a child -- walk up to find the root ancestor (top-level category)
      const optionsById = new Map(options.map((o) => [o.id, o]));
      let rootAncestor = selected;
      while (rootAncestor.groupId) {
         const parentId = rootAncestor.groupId.replace(NEXT_GROUP_ID_PREFIX, "");
         const parent = optionsById.get(parentId);
         if (!parent) break;
         rootAncestor = parent;
      }

      setFieldValue("category", { id: rootAncestor.id, label: rootAncestor.label });
      setFieldValue("subcategory", { id: selected.id, label: selected.label });
   } else {
      // Selected item is a top-level category
      setFieldValue("category", { id: selected.id, label: selected.label });
      setFieldValue("subcategory", null);
   }
};

export const getCommonFieldsForRequestAndAssignment = (
   values: AssignmentFormValues | RequestFormValues,
   customFields: CustomField[] | null,
): CommonPayloadForRequestAndAssignment => ({
   project_id: values.project!.id,
   category_id: values.category?.id ?? null,
   subcategory_id: values.subcategory?.id ?? null,
   start_day: getDetachedDay(new Date(values.start_date!)),
   end_day: getDetachedDay(new Date(values.end_date!)),
   start_time: values.assignment_by_time ? values.start_time.id : null,
   end_time: values.assignment_by_time ? values.end_time.id : null,
   percent_allocated: values.assignment_by_allocation ? values.percent_allocated : null,
   status_id: values.status?.id ?? null,
   work_days: values.work_days,
   custom_fields: customFields ? processCustomFields(customFields, values) : {},
   instruction_text: values.instruction_text ?? null,
   work_scope_text: values.work_scope_text ?? null,
   comments: values.comments
      ? [
           {
              author_id: values.author_id ?? "",
              content: values.comments,
           },
        ]
      : undefined,
});

export const getTimeOptionsFromProject = (
   selectedItem: ProjectOptions | undefined,
   defaultTime: number,
   timeKey: BaggageTimeKey,
) => {
   if (selectedItem) {
      const timeId = selectedItem.baggage[timeKey] ?? defaultTime;
      const timeLabel = timeOptions.find(({ id }) => id === timeId)?.label;
      return { id: timeId, label: timeLabel };
   }
};

export const getTotalHours = (endTime: number, startTime: number): number => {
   const totalHours = endTime >= startTime ? endTime - startTime : 24 + endTime - startTime;
   return totalHours;
};

// Function to transform overtime rates to use numeric keys
export const transformOvertimeRates = (
   overtimeRates: OverTimeRates | { [key: string]: number },
) => {
   const transformedRates: { [key: number]: number } = {};

   for (const [dayName, rate] of Object.entries(overtimeRates)) {
      const dayNumber =
         dayNameToNumberMap[dayName.toLowerCase() as keyof typeof dayNameToNumberMap];
      // Check if dayNumber is not undefined (include 0)
      if (dayNumber !== undefined) {
         // If rate is null or undefined, set it to 0
         transformedRates[dayNumber] = rate ?? 0;
      }
   }

   return transformedRates;
};

// Function to reverse transform overtime rates to use day names
export const reverseTransformOvertimeRates = (transformedRates: { [key: number]: number }) => {
   const originalRates: { [key: string]: number } = {};

   for (const [dayNumber, rate] of Object.entries(transformedRates)) {
      const dayName = numberToDayNameMap[Number(dayNumber) as keyof typeof numberToDayNameMap];

      // Check if dayName is defined in the map
      if (dayName !== undefined) {
         // Assign to the original structure using day names
         originalRates[dayName] = rate ?? 0;
      }
   }

   return originalRates;
};

export const getFileDimensions = (file: string) => {
   return new Promise<{ width: number; height: number }>((resolve) => {
      const img = new Image();
      img.src = file;
      img.addEventListener("load", () => resolve({ width: img.width, height: img.height }));
   });
};

export const resizeFile = (
   file: File,
   maxSize: number,
   minSize?: number,
   outputType: string = "file",
) => {
   return new Promise((resolve) => {
      Resizer.imageFileResizer(
         file,
         maxSize,
         maxSize,
         "png",
         100,
         0,
         (uri) => {
            resolve(uri);
         },
         outputType,
         minSize ?? undefined,
         minSize ?? undefined,
      );
   });
};

export const readFile = (file: File) => {
   return new Promise((resolve) => {
      const reader = new FileReader();
      reader.addEventListener("load", () => resolve(reader.result), false);
      reader.readAsDataURL(file);
   });
};

export const readFileFromUrl = async (name: string, url: string) => {
   const response = await fetch(url);
   const blob = await response.blob();
   return new File([blob], name ?? "file_name.png", {
      type: blob.type,
   });
};

export function prepareAttachmentsPayload(
   attachments: SerializedAttachment[],
   deletedAttachmentIds: string[],
) {
   const payload: AttachmentsData = {
      attachment_ids: getUniqueAttachmentIds(attachments, deletedAttachmentIds),
   };

   return payload;
}

export function getUniqueAttachmentIds(
   attachments: SerializedAttachment[],
   deletedAttachmentIds: string[],
) {
   // Get array of attachment IDs from attachments by user
   const addedAttachmentIds = attachments.map((attachment) => attachment.id);

   // addedAttachmentIds, and convert to Set for uniqueness to remove duplicates if any
   let uniqueAttachmentIds: any = new Set([...addedAttachmentIds]);

   // Remove IDs that are in deletedAttachmentIds array
   deletedAttachmentIds.forEach((id) => {
      uniqueAttachmentIds.delete(id);
   });

   // Convert Set back to array
   uniqueAttachmentIds = Array.from(uniqueAttachmentIds);

   return uniqueAttachmentIds;
}

export function prepareTagPayload(
   attachments: SerializedAttachment[],
   deletedAttachmentIds: string[],
   selectedTagId: string,
   expirationDate?: Date | null,
) {
   const payload: TagData = {
      tag_instances: {
         [selectedTagId]: {
            attachment_ids: getUniqueAttachmentIds(attachments, deletedAttachmentIds),
            expr_date: expirationDate ? getDetachedDay(expirationDate) : null,
         },
      },
   };

   return payload;
}
export const prepareActivitiesPayload = (
   entityId: string,
   entityType: string,
   includedCategories: string[] | string,
   limitActivities: number,
   nextStartingAfter?: string,
) => {
   const payload = {
      entity_id: entityId,
      entity_type: entityType as ActivityEntityType,
      included_categories: includedCategories as ActivityCategory[],
      limit: limitActivities,
      starting_after: nextStartingAfter,
   };
   return payload;
};

export function formatDate(date?: number, locale?: string): string {
   if (!date) return "";
   const isTimestamp = date.toString().length > 8;
   const dateFull = isTimestamp ? new Date(date) : getAttachedDate(date);
   const localDate = dateFull.toLocaleDateString(locale);
   return localDate;
}

export const formatCurrency = (value?: number, I18n?: any): string => {
   if (!value) {
      return "";
   }
   const currencyOptions = { locale: I18n.currentLocale(), currencyIsoCode: "USD" };
   const currencyFormatter = new CurrencyFormatter(currencyOptions);
   return currencyFormatter.formatCurrency(value);
};

export const getTranslationLabel = (label: string, I18n: any): string => {
   const translationItem = translationsLabelsActivityCard.find((item) => item.name === label);
   return translationItem ? I18n.t(translationItem.translation) : label;
};

export const customFieldsCard = (data: ActivitiesResponseProps, I18n: any) => {
   const { custom_field_name, custom_field_type, new_val, old_val } = data.meta ?? {};

   if (!data.meta) return null;

   switch (custom_field_type) {
      case CustomFieldType.TEXT:
      case CustomFieldType.NUMBER:
      case CustomFieldType.SELECT:
      case CustomFieldType.MULTI_SELECT:
         if (new_val !== null && old_val !== null)
            return renderChange(custom_field_name as string, I18n, old_val, new_val);
         if (new_val === null && old_val !== undefined)
            return renderRemove(custom_field_name as string, old_val, I18n);
         if (old_val === null && new_val !== undefined)
            return renderAdd(custom_field_name as string, new_val, I18n);
         break;
      case CustomFieldType.DATE:
         if (new_val !== null && old_val !== null)
            return renderChange(
               custom_field_name as string,
               I18n,
               formatDate(old_val as number),
               formatDate(new_val as number),
            );
         if (new_val === null && old_val !== null)
            return renderRemove(custom_field_name as string, formatDate(old_val as number), I18n);
         if (old_val === null && new_val !== null)
            return renderAdd(custom_field_name as string, formatDate(new_val as number), I18n);
         break;
      case CustomFieldType.BOOL: {
         const yesValue = I18n.t("views.company.workforce_planning.boolean_values.yes_value");
         const noValue = I18n.t("views.company.workforce_planning.boolean_values.no_value");
         if (new_val !== null && old_val !== null) {
            return renderChange(
               custom_field_name as string,
               I18n,
               old_val ? yesValue : noValue,
               new_val ? yesValue : noValue,
            );
         }
         if (new_val === null && old_val !== null) {
            return renderRemove(custom_field_name as string, old_val ? yesValue : noValue, I18n);
         }
         if (old_val === null && new_val !== null) {
            return renderAdd(custom_field_name as string, new_val ? yesValue : noValue, I18n);
         }
         break;
      }
      case CustomFieldType.CURRENCY:
         if (new_val !== null && old_val !== null)
            return renderChange(
               custom_field_name as string,
               I18n,
               formatCurrency(old_val as number, I18n),
               formatCurrency(new_val as number, I18n),
            );
         if (new_val === null && old_val !== null)
            return renderRemove(
               custom_field_name as string,
               formatCurrency(old_val as number, I18n),
               I18n,
            );
         if (old_val === null && new_val !== null)
            return renderAdd(
               custom_field_name as string,
               formatCurrency(new_val as number, I18n),
               I18n,
            );
         break;
      case CustomFieldType.HEX_COLOR:
         if (new_val !== null && old_val !== null)
            return renderColorChange(
               custom_field_name as string,
               I18n,
               old_val as string,
               new_val as string,
            );
         if (new_val === null && old_val !== undefined)
            return renderColorRemove(custom_field_name as string, old_val as string, I18n);
         if (old_val === null && new_val !== undefined)
            return renderColorAdd(custom_field_name as string, new_val as string, I18n);
         break;
      default:
         return null;
   }
};

export const groupByCategories = (items: any[]) => {
   const groups: { [key: string]: any[] } = {};

   if (!items.length) return groups;

   items.forEach((item: any) => {
      const categories = item.categories.length > 0 ? item.categories : ["All Tags"];

      categories.forEach((category: string) => {
         if (!groups[category]) {
            groups[category] = [];
         }
         groups[category].push(item);
      });
   });
   return groups;
};

export function updateStartDateIfFuture(
   projectStartDate: Date | null,
   isCreateFlow: boolean,
   setInitialValues: Dispatch<SetStateAction<any>>,
   page?: PageTitle,
) {
   if (page === PageTitle.SCHEDULE_CARD) {
      setInitialValues((prevValues: AssignmentFormValues | RequestFormValues) => ({
         ...prevValues,
         start_date: new Date(),
      }));
   }
   if (projectStartDate && isCreateFlow && isAfter(projectStartDate, new Date())) {
      setInitialValues((prevValues: AssignmentFormValues | RequestFormValues) => ({
         ...prevValues,
         start_date: projectStartDate,
      }));
   }
}

export function updateProjectEndDate(
   projectEndDate: Date | null,
   isCreateFlow: boolean,
   setInitialValues: Dispatch<SetStateAction<any>>,
) {
   setInitialValues((prevValues: AssignmentFormValues | RequestFormValues) => ({
      ...prevValues,
      end_date: null, // Reset end date to null initially
   }));
   if (projectEndDate && isCreateFlow) {
      const endDateToUse = getValidEndDate(projectEndDate);
      setInitialValues((prevValues: AssignmentFormValues | RequestFormValues) => ({
         ...prevValues,
         end_date: endDateToUse,
      }));
   }
}

// Determines the appropriate end date to use, defaulting to today if the project end date is in the past

export function getValidEndDate(estEndDate: Date | number | null | undefined): Date | null {
   if (!estEndDate) return null;

   const projectEndDate = new Date(estEndDate);
   const today = new Date();
   today.setHours(0, 0, 0, 0);

   return isAfter(today, projectEndDate) ? today : projectEndDate;
}

export function isEndDateInPast(endDate: Date | number | null | undefined): boolean {
   if (!endDate) return false;
   const date = new Date(endDate);
   const today = new Date();
   today.setHours(0, 0, 0, 0);
   return isAfter(today, date);
}

export const deepEqual = (a: any, b: any): boolean => {
   if (a === b) return true;

   // Handle NaN comparison
   if (typeof a === "number" && typeof b === "number" && isNaN(a) && isNaN(b)) {
      return true;
   }

   // Ensure type consistency between arrays and objects
   if (Array.isArray(a) !== Array.isArray(b)) return false;

   // Handle Date comparison
   if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
   }

   // Type mismatch check
   if (typeof a !== typeof b) return false;

   // Handle object and array comparison
   if (a && b && typeof a === "object") {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      // Check if both objects/arrays have the same number of keys
      if (keysA.length !== keysB.length) return false;

      // Use a Set for faster lookup instead of includes
      const keysBSet = new Set(keysB);

      for (const key of keysA) {
         if (!keysBSet.has(key) || !deepEqual(a[key], b[key])) {
            return false;
         }
      }

      return true;
   }

   return false;
};

export const validateWorkDayTimeAndAllocationForBulkAssignmentAndRequest = (
   data: AssignmentListData | RequestListData,
   formStartDay: number | null,
   formEndDay: number | null,
   isFormByAllocation: boolean,
   isFormByTime: boolean,
   I18n: ReturnType<typeof useI18nContext>,
): string[] => {
   const reasons: string[] = [];

   // Workday validation
   if (formStartDay !== null) {
      const dayOfWeek = getAttachedDate(formStartDay).getDay();
      if (!data.work_days_object?.[dayOfWeek as keyof AssignmentWorkDays]) {
         reasons.push(
            `${I18n.t(
               "views.company.workforce_planning.assignment.bulk.edit.validation.work_days",
               { field: I18n.t("views.company.workforce_planning.start_date") },
            )} ${data.work_days}`,
         );
      }
   }

   if (formEndDay !== null) {
      const dayOfWeek = getAttachedDate(formEndDay).getDay();
      if (!data.work_days_object?.[dayOfWeek as keyof AssignmentWorkDays]) {
         reasons.push(
            `${I18n.t(
               "views.company.workforce_planning.assignment.bulk.edit.validation.work_days",
               { field: I18n.t("views.company.workforce_planning.end_date") },
            )} ${data.work_days}`,
         );
      }
   }

   // Time and allocation validation
   if (isFormByAllocation && (data.start_time || data.end_time)) {
      reasons.push(
         I18n.t(
            "views.company.workforce_planning.assignment.bulk.edit.validation.percent_allocation",
         ),
      );
   }

   if (isFormByTime && data.percent_allocated) {
      reasons.push(
         I18n.t(
            "views.company.workforce_planning.assignment.bulk.edit.validation.start_time_end_time",
         ),
      );
   }

   return reasons;
};

export function calculateTbdDate(startDate: Date, companyTbdWeeks: number): Date {
   // Add weeks to the start date
   const intermediateDate = addWeeks(new Date(startDate), companyTbdWeeks);
   // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
   const dayOfWeek = intermediateDate.getDay();

   // Adjust to the last workday (Friday = 5)
   let adjustment = 0;
   if (dayOfWeek === 6 || dayOfWeek === 0) {
      // If the day is Saturday (6) or Sunday (0), move back to the previous Friday
      adjustment = dayOfWeek === 6 ? 1 : 2;
   }

   // Adjust the date to the last workday
   const endDate = new Date(intermediateDate);
   endDate.setDate(intermediateDate.getDate() - adjustment);

   return endDate;
}
